// @ts-check
// Peças visuais comuns das conversas: ícone do canal, nome e identificador do contato e tag de etapa.
import { html } from 'htm/preact';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { findOption, formatFullName } from '../../lib/field-metadata.js';
import { formatPhoneHandle } from '../../lib/conversation-format.js';
import { Tag } from '../../ui/chip.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/** Verde da marca do WhatsApp, usado só no selo do canal. */
const WHATSAPP_GREEN = '#25D366';

/**
 * Selo do canal: balão de conversa genérico em verde para o WhatsApp (sem o logotipo da Meta)
 * e envelope para o e-mail.
 * @param {{ channelType: string, size?: number, className?: string }} props
 */
export function ChannelIcon({ channelType, size = 16, className = '' }) {
  const label = t(`conversations.channels.${channelType === 'EMAIL' ? 'EMAIL' : 'WHATSAPP'}`);
  if (channelType === 'EMAIL') {
    return html`<svg class=${`channel-icon ${className}`} width=${size} height=${size} viewBox="0 0 16 16" role="img" aria-label=${label}>
      <title>${label}</title>
      <circle cx="8" cy="8" r="8" fill="var(--t-color-blue)" />
      <rect x="4" y="5.2" width="8" height="5.6" rx="1" fill="none" stroke="#fff" stroke-width="1.2" />
      <path d="M4.4 5.8 8 8.4l3.6-2.6" fill="none" stroke="#fff" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`;
  }
  return html`<svg class=${`channel-icon ${className}`} width=${size} height=${size} viewBox="0 0 16 16" role="img" aria-label=${label}>
    <title>${label}</title>
    <circle cx="8" cy="8" r="8" fill=${WHATSAPP_GREEN} />
    <path d="M8 3.7c-2.43 0-4.4 1.72-4.4 3.85 0 1.16.58 2.2 1.5 2.9l-.42 1.75 1.86-.98c.46.12.95.18 1.46.18 2.43 0 4.4-1.72 4.4-3.85S10.43 3.7 8 3.7z" fill="#fff" />
    <circle cx="6.1" cy="7.55" r=".62" fill=${WHATSAPP_GREEN} />
    <circle cx="8" cy="7.55" r=".62" fill=${WHATSAPP_GREEN} />
    <circle cx="9.9" cy="7.55" r=".62" fill=${WHATSAPP_GREEN} />
  </svg>`;
}

/**
 * @param {unknown} name nome composto ({ firstName, lastName }) ou texto
 */
export function formatPersonName(name) {
  if (!name) return '';
  if (typeof name === 'string') return name;
  return formatFullName(/** @type {{ firstName?: string, lastName?: string }} */ (name));
}

/**
 * Identificador legível do contato: telefone formatado no WhatsApp, e-mail no e-mail.
 * @param {import('../../data/conversations.js').ConversationThread} thread
 */
export function getContactHandleLabel(thread) {
  const handle = thread.contact?.handle ?? '';
  return thread.channelType === 'WHATSAPP' ? formatPhoneHandle(handle) : handle;
}

/**
 * Nome exibido: pessoa vinculada, nome do perfil do contato ou o identificador.
 * @param {import('../../data/conversations.js').ConversationThread} thread
 */
export function getThreadDisplayName(thread) {
  return formatPersonName(thread.person?.name) || thread.contact?.displayName || getContactHandleLabel(thread);
}

/**
 * Tag da etapa do card com a cor das opções do campo stage.
 * @param {{ stage: string }} props
 */
export function StageTag({ stage }) {
  const objectMetadata = objectsByNameSingular.value.get('enrollmentOpportunity');
  const stageField = objectMetadata?.fields.find((field) => field.name === 'stage');
  const option = stageField ? findOption(stageField, stage) : null;
  return html`<${Tag} color=${option?.color ?? 'gray'} text=${option ? translateMetadataLabel(option.label) : stage} />`;
}
